import CustomTooltip from '../../shared/CustomTooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Grid, Stack, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    useAddNirovisionConnectionMutation,
    useGetNirovisionConnectionQuery,
} from '../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../shared/AuthRoles';
import toast from '../../shared/Toast';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { handleHttpError } from '../../../shared/utils';
import FormTextField from '../../shared/FormTextField';
import LoadingComponent from '../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';

const NirovisionConnectionSettings = () => {
    const loggedInUser = UserRole();
    const navigate = useNavigate();

    useEffect(() => {
        if (loggedInUser?.role.includes('guest')) {
            navigate(`/permissionDenied`);
        }
    }, []);

    const { data: nirovisionSettings, isLoading } =
        useGetNirovisionConnectionQuery('/nirovision/connection', {
            skip: loggedInUser.role === 'guest',
        });

    const [addNirovisionDetails] = useAddNirovisionConnectionMutation();

    const schema = z.object({
        clientId: z.coerce.string(),
        clientSecret: z.coerce.string(),
    });
    type NirovisionSettings = z.infer<typeof schema>;
    const {
        reset,
        control,
        handleSubmit,
        formState: { isSubmitting },
    } = useForm<NirovisionSettings>({
        resolver: zodResolver(schema),
    });

    useEffect(() => {
        if (nirovisionSettings) {
            const { clientId, clientSecret } = nirovisionSettings;
            reset({ clientId, clientSecret });
        }
    }, [nirovisionSettings]);

    const onSubmit = async (data: NirovisionSettings) => {
        addNirovisionDetails(data)
            .unwrap()
            .then((data: any) => {
                if (data?.error) {
                    const errorMessage = handleHttpError(data?.error);
                    toast.error(errorMessage);
                } else {
                    toast.success(`Successfully updated nirovision settings`);
                }
            })
            .catch(() => {
                toast.error(
                    'Failed to update nirovision settings. Please try again.',
                );
            });
    };

    return isLoading ? (
        <LoadingComponent />
    ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '40px',
                }}
            >
                <Grid
                    sx={{
                        width: '100%',
                        justifyContent: 'left',
                        display: 'flex',
                    }}
                >
                    <Stack spacing={2}>
                        <Typography
                            variant='h1'
                            sx={{
                                textAlign: 'left',
                                textTransform: 'uppercase',
                            }}
                        >
                            Nirovision Connection Settings
                        </Typography>
                        {nirovisionSettings?.clientName ? (
                            <Typography
                                color='text.primary'
                                sx={{
                                    fontSize: '14px',
                                }}
                            >
                                Connected Nirovision API Client:
                            </Typography>
                        ) : (
                            <Typography
                                color='text.primary'
                                sx={{
                                    fontSize: '14px',
                                }}
                            >
                                The following values can be retrieved from your{' '}
                                <Link
                                    style={{
                                        textDecoration: 'underline',
                                    }}
                                    target='_blank'
                                    to={'https://auth.nirovision.com/'}
                                >
                                    Nirovision account.
                                </Link>
                            </Typography>
                        )}
                    </Stack>
                </Grid>

                <Grid container spacing={2}>
                    {nirovisionSettings?.clientName ? (
                        <Grid item container columns={25} spacing={1}>
                            <Grid
                                item
                                xs={12}
                                lg={5}
                                sx={{ display: 'flex', gap: '2px' }}
                            >
                                <Typography color='text.primary'>
                                    Client Name
                                </Typography>
                            </Grid>
                            <Grid item xs={25} lg={9}>
                                <FormTextField
                                    disabled
                                    placeholder={nirovisionSettings?.clientName}
                                    type='string'
                                    name='clientName'
                                    control={control}
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <>
                            <Grid item container columns={25} spacing={1}>
                                <Grid
                                    item
                                    xs={12}
                                    lg={5}
                                    sx={{ display: 'flex', gap: '2px' }}
                                >
                                    <Typography color='text.primary'>
                                        Client Id
                                    </Typography>
                                    <Grid>
                                        <CustomTooltip
                                            title={
                                                <Typography>
                                                    Get your Client ID and
                                                    Secret by logging into your
                                                    Nirovision account, select
                                                    'Settings' from the menu.
                                                    Choose 'API & Integrations'
                                                    from the left panel. Click
                                                    on 'API Clients' and select
                                                    'Add API Client'. Enter
                                                    '7DAY' as the name and click
                                                    'Create'. Ensure you save
                                                    your Client ID and Secret
                                                    Key somewhere safe as it
                                                    will only be displayed once.
                                                </Typography>
                                            }
                                            placement='right'
                                        >
                                            <HelpOutlineIcon
                                                sx={{
                                                    color: 'text.primary',
                                                    fontSize: '16px',
                                                }}
                                            />
                                        </CustomTooltip>
                                    </Grid>
                                </Grid>
                                <Grid item xs={25} lg={9}>
                                    <FormTextField
                                        name='clientId'
                                        control={control}
                                        type='string'
                                    />
                                </Grid>
                            </Grid>
                            <Grid item container columns={25} spacing={1}>
                                <Grid
                                    item
                                    xs={12}
                                    lg={5}
                                    sx={{ display: 'flex', gap: '2px' }}
                                >
                                    <Typography color='text.primary'>
                                        Client Secret
                                    </Typography>
                                    <Grid>
                                        <CustomTooltip
                                            title={
                                                <Typography>
                                                    Get your Client ID and
                                                    Secret by logging into your
                                                    Nirovision account, select
                                                    'Settings' from the menu.
                                                    Choose 'API & Integrations'
                                                    from the left panel. Click
                                                    on 'API Clients' and select
                                                    'Add API Client'. Enter
                                                    '7DAY' as the name and click
                                                    'Create'. Ensure you save
                                                    your Client ID and Secret
                                                    Key somewhere safe as it
                                                    will only be displayed once.
                                                </Typography>
                                            }
                                            placement='right'
                                        >
                                            <HelpOutlineIcon
                                                sx={{
                                                    color: 'text.primary',
                                                    fontSize: '16px',
                                                }}
                                            />
                                        </CustomTooltip>
                                    </Grid>
                                </Grid>
                                <Grid item xs={25} lg={9}>
                                    <FormTextField
                                        name='clientSecret'
                                        control={control}
                                        type='string'
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}

                    <Grid item container columns={25} spacing={1}>
                        <Grid
                            item
                            xs={25}
                            lg={14}
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <CustomButton
                                type='submit'
                                variant='contained'
                                color='primary'
                                loading={isSubmitting}
                                disabled={
                                    isSubmitting || nirovisionSettings?.clientId
                                }
                            >
                                Save
                            </CustomButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </form>
    );
};

export default NirovisionConnectionSettings;
